import { gridFilterModelSelector } from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import { isValidValue } from "../filterUtils";
import { EditableGridStateColDef } from "../types";

export const buildWhereFromFilters = <E extends keyof EntityMappings>({
  columns,
  filterModel,
  variables,
}: {
  columns: EditableGridStateColDef[];
  filterModel: ReturnType<typeof gridFilterModelSelector>;
  variables: { where?: EntityMappings[E]["filter"] };
}) => {
  const filterPre = filterModel?.items?.reduce<Array<EntityMappings[E]["filter"]>>(
    (where, { field, operator, value }) => {
      const column = columns.find((c) => c.field === field);
      if (!column?.remoteFilter?.filterPath || !operator || !column.type || !isValidValue(value, column.type))
        return where;
      const filterFunc = column.remoteFilter?.filterPath ?? function () {};

      return where.concat(
        filterFunc({
          where,
          filterValue: { [operator]: value },
        }) as EntityMappings[E]["filter"]
      );
    },
    []
  );
  const quickValues = (filterModel.quickFilterValues ?? []) as string[];

  const fuzzyFilterValues = columns.reduce<unknown[]>((where, { type, remoteFilter }) => {
    if (!quickValues.length) return where;
    if (type === "string" && remoteFilter) {
      const filterFunc = remoteFilter?.filterPath ?? function () {};
      const condition = quickValues.map((value) => ({ contains: value }));
      return where.concat(
        filterFunc({
          where,
          filterValue: { or: condition },
        }) as EntityMappings[E]["filter"]
      );
    }
    if (type === "stringArray" && remoteFilter) {
      const filterFunc = remoteFilter?.filterPath ?? function () {};
      const condition = { contains: quickValues[0] };
      return where.concat(
        filterFunc({
          where,
          filterValue: { or: condition },
        }) as EntityMappings[E]["filter"]
      );
    }
    return where;
  }, []);

  const fuzzyFilter = { or: [...fuzzyFilterValues] } as EntityMappings[E]["filter"];
  const filter = fuzzyFilterValues.length
    ? filterPre.length
      ? filterPre.concat(fuzzyFilter)
      : [fuzzyFilter]
    : filterPre;

  const combinator = variables.where?.[filterModel.logicOperator ?? "and"];
  const nextWhere = filter.length ? [...(combinator ?? []), ...filter] : undefined;

  const where = nextWhere ? { ...variables.where, [filterModel.logicOperator ?? "and"]: nextWhere } : variables.where;
  return where;
};
